import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IndeterminateProgressCircle } from '../blocks/IndeterminateProgressCircle';
import { PROGRESS_CIRCLE_SIZE } from '../constants';
import { getCircleSize, getLabelSize } from '../shared-styles';

// the container to hold both circle and label
const ProgressCircleContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${({ $isInline }) => ($isInline ? 'row' : 'column')};
  gap: ${({ $isInline }) => ($isInline ? '8px' : '4px')};
`;

// label styles
const StyledLabel = styled.span`
  color: ${({ theme }) => theme.color.additional.dark.value};
  font-family: Roboto, sans-serif;
  font-size: ${({ $labelSize }) => Math.max($labelSize, 14) + 'px'};
  font-weight: 500;
  letter-spacing: 0.25px;
  line-height: auto;
`;

export const IndeterminateCircleLoader = ({
  ariaLabel = 'Loading',
  className,
  dataTestId,
  hideLabel = false,
  isInline = true,
  label = 'Loading...',
  size = PROGRESS_CIRCLE_SIZE.LARGE,
}) => {
  return (
    <ProgressCircleContainer
      className={className || undefined}
      data-testid={dataTestId || undefined}
      $isInline={isInline}
    >
      <IndeterminateProgressCircle
        ariaLabel={ariaLabel}
        className={className ? `${className}-progress-circle` : undefined}
        dataTestId={dataTestId ? `${dataTestId}-progress-circle` : undefined}
        size={size}
      />
      {!hideLabel && (
        <StyledLabel
          aria-label={`${ariaLabel} label`}
          className={className ? `${className}-label` : undefined}
          data-testid={dataTestId ? `${dataTestId}-label` : undefined}
          $labelSize={getLabelSize(getCircleSize(size))}
        >
          {label}
        </StyledLabel>
      )}
    </ProgressCircleContainer>
  );
};

IndeterminateCircleLoader.propTypes = {
  /** Sets aria Label for the progress circle */
  ariaLabel: PropTypes.string,
  /** Adds additional class name to progress circle track */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Boolean to hide the label (default false) */
  hideLabel: PropTypes.bool,
  /** Boolean to switch between inline and non inline (default true) */
  isInline: PropTypes.bool,
  /** Sets the label for the progress circle */
  label: PropTypes.string,
  /** Sets the size of the loader to one of 4 predefined sizes */
  size: PropTypes.oneOf(Object.values(PROGRESS_CIRCLE_SIZE)),
};
